import { GlobalStateController } from "bai-react-global-state";
import _ from "lodash";

import getLogger, { LogGroup } from "js/core/logger";
import { DEFAULT_AI_IMAGEMODEL, DEFAULT_AI_IMAGEQUALITY, MAX_KEPT_SLIDE_TYPES } from "../../../../common/aiConstants";
import { AuthoringBlockType, TextStyleType } from "../../../../common/constants";
import { MetricName } from "../../../../common/interfaces/index";
import { findElementsWithImages } from "../../../../common/utils/findElementsWithImages";
import getObjectHash from "../../../../common/utils/getObjectHash";
import resolveImageSource, { resolveFallbackImageSource } from "../../../../common/utils/resolveImageSource";
import WorkspaceController from "../../../controllers/WorkspaceController";
import { applySlideImage, resetSlideImage } from "../../../core/models/slide";
import AiGenerationService from "../../../core/services/aiGeneration";
import { getTextFromBlocks } from "../../../core/utilities/htmlTextHelpers";
import { ShowWarningDialog } from "../../../react/components/Dialogs/BaseDialog";
import { uuid } from "common/utils/uuid";
import { getSlideContentHash, hasEditsSinceAiAuthored } from "./aiAuthoredContent";
import { getIdeasFreshness } from "./ideaFreshness";

const logger = getLogger(LogGroup.AI);

const initialState = {
    currentSlide: null,
    generatedIdeas: [],
    isGenerating: false,
    slideIdeas: [],
    slideCategories: [],
    slideImage: null,
    files: [],
    // True once the slide has drifted far enough from the text these ideas were generated from that
    // their thumbnails no longer represent it. Computed by comparing text — no AI call.
    ideasAreStale: false,
    ideasDivergence: 0
};

const SEPARATOR = "&nbsp;&nbsp;&nbsp;";

/**
 * The ideas panel rebuilds its previews from metadata.generatedIdeas every time it opens, and the
 * expanded models always come back with empty images - a preview's generated image is not stored on
 * the idea. Keying the resolved asset by what the image is *of* lets a rebuild restore it instead of
 * generating a new one, which for Team Assets means re-running (and re-rolling) the selection.
 */
function getGeneratedImageKey(element) {
    const { search = null, searchStock = null, assetType = null, imageSource = null } = element.__generateImage ?? {};
    if (!search && !searchStock) {
        return null;
    }

    // Scoped to the source it came from, so switching sources can't restore the previous source's image
    return getObjectHash({ search, searchStock, assetType, imageSource });
}

function collectGeneratedImages(model) {
    const images = {};

    for (const element of findElementsWithImages(model ?? {})) {
        // The slide image has its own home in metadata.generatedModel.slideImage
        if (element.__generateImage?.isSlideImage || !element.content_value) {
            continue;
        }

        const key = getGeneratedImageKey(element);
        if (key) {
            images[key] = element.content_value;
        }
    }

    return images;
}

function getSourcesHtml(sources, html = "") {
    let titleCounts = {};
    let dedupedSources = _.cloneDeep(sources);
    dedupedSources = _.sortBy(dedupedSources, "title");
    dedupedSources.forEach(source => {
        titleCounts[source.title] = (titleCounts[source.title] || 0) + 1;
        if (titleCounts[source.title] > 1) {
            source.title = `${source.title} (${titleCounts[source.title]})`;
        }
    });

    let htmlChunks = [];
    for (let source of dedupedSources) {
        htmlChunks.push(`<a href="${source.url}" target="_blank" rel="noopener noreferrer">${source.title}</a>`);
    }

    return [html, ...htmlChunks].filter(Boolean).join(SEPARATOR);
}

export class SlideAiController extends GlobalStateController {
    get currentSlide() {
        return this._state.currentSlide;
    }

    getCategoryController() {
        return import(/* webpackMode: "eager" */ "common/SlideCategories/CategoryController").then(module => module.CategoryController);
    }

    async setCurrentSlide(slide, canvasController) {
        if (this._state.currentSlide) {
            this._state.currentSlide.off("change:metadata", this.onMetadataUpdated);
        }

        if (!slide) {
            await this.reset();
            return;
        }

        let metadata = slide.getMetadata();

        this.generatingImagesFor = null;

        await this._updateState({
            ...initialState,
            currentSlide: slide,
            canvasController,
            generatedIdeas: metadata.generatedIdeas || [],
            slideImage: metadata.generatedModel?.slideImage || null,
            slideCategories: [],
            isGenerating: false,
            generateError: metadata.generateError,
        });

        await this.resetFiles();

        slide.on("change:metadata", this.onMetadataUpdated);

        this.buildSlideModels();
    }

    async reset() {
        await this._updateState(() => ({
            ...initialState,
        }));
    }

    get ideasAreStale() {
        return this._state.ideasAreStale;
    }

    /**
     * Free staleness check: `metadata.slidePrompt` is the slide text these ideas were generated from,
     * so this is a text comparison. Called from the consultant's idle tick rather than on every
     * keystroke.
     *
     * `contentHash` is passed in by the consultant, which has already built the inventory for its own
     * analysis; it is only recomputed here for callers that haven't.
     */
    async updateIdeasFreshness(canvas, contentHash = undefined) {
        const { currentSlide, generatedIdeas } = this._state;
        if (!currentSlide || !canvas) {
            return;
        }

        const metadata = currentSlide.getMetadata();
        const { isStale, divergence } = getIdeasFreshness({
            currentPrompt: this.buildPromptFromSlide(canvas),
            generatedPrompt: metadata.slidePrompt ?? metadata.originalSlidePrompt,
            hasIdeas: generatedIdeas.length > 0,
            // Never offer to refresh ideas for a slide the user hasn't touched. The ideas and the
            // slide were produced by the same generation, so "you've edited this since" would be
            // both wrong and an invitation to spend a request replacing them with equivalents.
            hasHumanEdits: hasEditsSinceAiAuthored(
                currentSlide,
                contentHash ?? getSlideContentHash(canvas)
            )
        });

        if (isStale === this._state.ideasAreStale && divergence === this._state.ideasDivergence) {
            return;
        }

        await this._updateState({ ideasAreStale: isStale, ideasDivergence: divergence });
    }

    /**
     * Regenerates ideas after the slide has been edited, keeping the layout directions the user was
     * already weighing up (rebuilt against the current text, so the thumbnails stop lying) and letting
     * the model propose different ones alongside them.
     */
    async refreshIdeas(canvas, options = {}) {
        const { currentSlide, generatedIdeas } = this._state;
        if (!currentSlide || !canvas) {
            return;
        }

        const metadata = currentSlide.getMetadata();

        // Highest-confidence directions first, so what we carry is what the model rated best.
        const keepSlideTypes = [...generatedIdeas]
            .sort((a, b) => (b.slideConfidence ?? 0) - (a.slideConfidence ?? 0))
            .map(idea => idea.slideCategory)
            .filter(Boolean)
            .slice(0, MAX_KEPT_SLIDE_TYPES);

        return this.regenerateIdeas(this.buildPromptFromSlide(canvas), {
            ...options,
            keepSlideTypes,
            previousSlidePrompt: metadata.slidePrompt ?? metadata.originalSlidePrompt,
            appliedSuggestions: metadata.aiAppliedSuggestions ?? [],
            // The words changed, not the visual direction — don't spend image credits.
            regenerateSlideImage: false,
            allowSlideImageFallback: false,
            usePresentationAsContext: metadata.presentationAsContext ?? true,
            language: metadata.language,
            preserveExactText: false
        });
    }

    async resetFiles() {
        await this._updateState({
            files: this.getFilesFromSlideMetadata()
        });
    }

    getFilesFromSlideMetadata() {
        let { currentSlide } = this._state;
        let metadata = currentSlide.getMetadata();
        return metadata.files ? metadata.files.map(f => f.hasBeenUsedFor === "slide" ? ({ ...f, hasBeenUsed: false }) : f) : [];
    }

    setIsGenerating(isGenerating) {
        this._updateState({ isGenerating });
    }

    onMetadataUpdated = async (slide, changes) => {
        if (this._state.currentSlide !== slide) {
            return;
        }

        const metadata = slide.getMetadata();

        if (changes.generatedIdeas) {
            await this._updateState({
                generatedIdeas: metadata.generatedIdeas || [],
                slideImage: metadata.generatedModel?.slideImage || null
            });

            this.buildSlideModels();
        } else if (changes.generatedModel?.slideImage) {
            // The slide image can finish server-side while the panel is open, so the rebuilt previews
            // need the new one - they read it from state, not from the slide
            await this._updateState({
                slideImage: metadata.generatedModel?.slideImage || null
            });

            this.buildSlideModels();
        }
    }

    buildPromptFromSlide(canvas) {
        let prompt = "";

        try {
            let header = canvas.getHeaderElement();
            if (header) {
                prompt += header.buildSlidePrompt() + "\n\n";
            }

            let primary = canvas.getPrimaryElement();
            if (primary) {
                prompt += primary.buildSlidePrompt() + "\n";
            }

            let sideBar = canvas.getSideBarElement();
            if (sideBar) {
                prompt += sideBar.buildSlidePrompt() + "\n";
            }

            let bottomTray = canvas.getTextTrayElement();
            if (bottomTray) {
                const trayText = getTextFromBlocks(bottomTray.model?.elementTextBlock).trim();
                if (trayText) {
                    prompt += "## Conclusion\n\n" + trayText + "\n";
                }
            }

            if (canvas.slide.getMetadata()?.generatedModel?.slideImage?.prompt) {
                prompt += "\nVisual: " + canvas.slide.getMetadata().generatedModel.slideImage.prompt + "\n";
            }
        } catch (err) {
            logger.error(err, "[SlideAiController] Error building prompt from slide");
        }

        return prompt;
    }

    async _waitForFilesToProcess() {
        let getFiles = () => this._state.files.filter(f => !f.hasBeenUsed);

        let processingFiles = getFiles().filter(f => f.isProcessing);
        while (processingFiles.length > 0) {
            await new Promise(resolve => setTimeout(resolve, 100));
            processingFiles = getFiles().filter(f => f.isProcessing);
        }

        return getFiles();
    }

    async regenerateIdeas(slidePrompt, {
        slideType,
        language,
        preserveExactText = false,
        usePresentationAsContext = true,
        webSearch = false,
        citeSources = false,
        imageSource = null,
        fallbackImageSource = null,
        regenerateSlideImage = true,
        imageStyle = null,
        imageModel = DEFAULT_AI_IMAGEMODEL,
        imageQuality = DEFAULT_AI_IMAGEQUALITY,
        keepSlideTypes = null,
        previousSlidePrompt = null,
        appliedSuggestions = null,
        allowSlideImageFallback = true
    } = {}) {
        imageModel = DEFAULT_AI_IMAGEMODEL;
        imageQuality = DEFAULT_AI_IMAGEQUALITY;

        const { currentSlide } = this._state;
        const startedAt = Date.now();
        const templateId = currentSlide?.get?.("templateId") ?? null;
        const imageCount = findElementsWithImages(currentSlide?.attributes ?? {}).length;
        let generationFailed = true;

        this._updateState({ isGenerating: true });

        try {
            const files = await this._waitForFilesToProcess();
            files.forEach(file => {
                file.hasBeenUsed = true;
                file.hasBeenUsedFor = "slide";
            });

            const genProps = {
                slidePrompt,
                slideType,
                language,
                preserveExactText,
                usePresentationAsContext,
                files,
                webSearch,
                citeSources
            };

            // Refresh-only context. Kept out of genProps deliberately: genProps is spread into the
            // slide's metadata, and these describe one particular regeneration rather than the state
            // of the slide. Persisting them would leave the next refresh reading its own stale echo.
            const refreshProps = {};
            if (keepSlideTypes?.length) {
                refreshProps.keepSlideTypes = keepSlideTypes;
            }
            if (previousSlidePrompt) {
                refreshProps.previousSlidePrompt = previousSlidePrompt;
            }
            if (appliedSuggestions?.length) {
                refreshProps.appliedSuggestions = appliedSuggestions;
            }

            imageSource = resolveImageSource(imageSource, WorkspaceController.actionPermissions);
            // Always resolved to something usable - callers that don't offer the choice (the Generate
            // Ideas dialog) used to leave this null, which left a Team Asset miss with no image at all
            fallbackImageSource = resolveFallbackImageSource(
                fallbackImageSource ?? currentSlide.getMetadata().fallbackImageSource,
                WorkspaceController.actionPermissions
            );

            // A refresh opts out of this: it re-derives layouts from edited text, and the visual
            // direction hasn't changed. Without the opt-out, any slide without an existing AI image —
            // the common case — would silently generate one on every "Refresh ideas".
            if (!regenerateSlideImage && allowSlideImageFallback && imageSource !== "none") {
                if (
                    // No slide image defined, have to generate anyway
                    !currentSlide.getMetadata().generatedModel?.slideImage ||
                    // Image source has changed, have to regenerate
                    currentSlide.getMetadata().imageSource !== imageSource ||
                    currentSlide.getMetadata().fallbackImageSource !== fallbackImageSource

                ) {
                    regenerateSlideImage = true;
                }
            }

            imageStyle = imageStyle || currentSlide.getMetadata().generatedModel?.slideImage?.imageStyle || currentSlide.getMetadata().imageStyle || "";

            const slideImagePromise = (async () => {
                if (!regenerateSlideImage) {
                    return { metadata: {} };
                }

                let { slideImagePrompt } = await AiGenerationService.generateSlideImagePrompt(
                    slidePrompt,
                    usePresentationAsContext ? currentSlide.presentation.get("metadata")?.topic : null
                );

                let { slide } = await AiGenerationService.regenerateSlideImage({
                    slide: currentSlide.attributes,
                    imagePrompt: slideImagePrompt,
                    imageStyle,
                    imageSource,
                    fallbackImageSource,
                    imageModel,
                    imageQuality,
                    replaceImages: false
                });

                return slide;
            })();

            let response;
            try {
                const isDummyPresentation = currentSlide.presentation.get("isDummy") === true;
                response = await AiGenerationService.generateSlideData({
                    slideId: currentSlide.id,
                    presentationId: isDummyPresentation ? undefined : currentSlide.presentation.id,
                    themeId: isDummyPresentation ? currentSlide.presentation.get("themeId") : undefined,
                    slideIndex: currentSlide.getIndex(),
                    presentationTopic: currentSlide.presentation.get("metadata")?.topic || "",
                    ...genProps,
                    usePresentationAsContext: isDummyPresentation ? false : genProps.usePresentationAsContext,
                    ...refreshProps
                });
            } catch (err) {
                logger.error(err, "[SlideAiController] Error generating slide data");
            }

            if (!response || !response.slides || response.slides.length == 0) {
                slideImagePromise.catch(err => logger.error(err, "[SlideAiController] Error generating slide image after slide data failed"));
                ShowWarningDialog({ title: "Slide AI", message: "Sorry, an error occurred while generating slide ideas. This may be a temporary issue so please try again." });

                this._updateState({ isGenerating: false });
                return;
            }

            if (this._state.currentSlide !== currentSlide) {
                slideImagePromise.catch(err => logger.error(err, "[SlideAiController] Error generating slide image after current slide changed"));
                return;
            }

            let generatedIdeas = response.slides.map(slide => slide.slideData);

            let savedFiles = [];
            if (files.length > 0) {
                ({ files: savedFiles } = await AiGenerationService.saveContextDocuments(files));
            }

            if (currentSlide.updatePromise) {
                await currentSlide.updatePromise;
            }

            currentSlide.update({
                hasBeenViewed: true,
                metadata: {
                    ...currentSlide.getMetadata(),
                    ...genProps,
                    sources: response.sources ?? currentSlide.getMetadata().sources ?? [],
                    imageSource,
                    fallbackImageSource,
                    imageStyle,
                    imageModel,
                    imageQuality,
                    generatedIdeas,
                    generatedIdeaImages: {},
                    files: savedFiles,
                    presentationAsContext: usePresentationAsContext,
                    gotUserFeedback: false,
                }
            }, { silent: true, saveUndo: false });

            if (regenerateSlideImage) {
                currentSlide.update(resetSlideImage(currentSlide.attributes, true), { silent: true, saveUndo: false });
            }

            await this._updateState({
                generatedIdeas,
                slideImage: currentSlide.getMetadata().generatedModel?.slideImage,
                // These ideas were just built from the current text.
                ideasAreStale: false,
                ideasDivergence: 0
            });

            this.generatingImagesFor = null;

            await this.buildSlideModels();

            this._updateState({ isGenerating: false });

            if (!regenerateSlideImage) {
                generationFailed = false;
                return;
            }

            let slideImageUpdates = { metadata: {} };
            try {
                slideImageUpdates = await slideImagePromise;
            } catch (err) {
                logger.error(err, "[SlideAiController] Error generating slide image");
            }

            if (this._state.currentSlide !== currentSlide) {
                return;
            }

            const generatedSlideImage = slideImageUpdates.metadata?.generatedModel?.slideImage;
            if (generatedSlideImage) {
                currentSlide.update(applySlideImage(currentSlide.attributes, generatedSlideImage), { silent: false, saveUndo: true });

                await this._updateState({
                    slideImage: currentSlide.getMetadata().generatedModel?.slideImage,
                });

                await this.buildSlideModels();
            }

            generationFailed = false;
        } catch (err) {
            this._updateState({ isGenerating: false });
            throw err;
        } finally {
            logger.metric(MetricName.AI_GENERATE_SLIDE_TIME, {
                generationTimeMs: Date.now() - startedAt,
                templateId,
                withContext: usePresentationAsContext,
                imageCount,
                generationFailed
            });
        }
    }

    async buildSlideModels() {
        const { currentSlide, generatedIdeas, slideImage } = this._state;

        if (!currentSlide) {
            await this._updateState({ slideCategories: [] });
            return;
        }

        let metadata = currentSlide.getMetadata();
        let imageSource = resolveImageSource(metadata?.imageSource, WorkspaceController.actionPermissions);
        let imageStyle = slideImage?.imageStyle ?? metadata?.imageStyle;
        let imageModel = slideImage?.imageModel ?? metadata?.imageModel;
        let imageQuality = slideImage?.imageQuality ?? metadata?.imageQuality;
        let generatedIdeaImages = {
            ...metadata?.generatedIdeaImages,
            // The slide's own images are what the user is looking at, so previews should match them
            ...collectGeneratedImages(currentSlide.attributes)
        };

        let slideCategories = [];
        for (let idea of generatedIdeas) {
            let slideModel = idea.slideModel;

            slideModel.slideImage = slideImage;

            // build slide models for this category
            let CategoryController = await this.getCategoryController();
            let category = CategoryController.getCategory(idea.slideCategory);
            if (category.validateGeneratedModel(slideModel) === false) {
                logger.warn(`[SlideAiController] Generated model for category ${idea.slideCategory} failed validation`);
                continue;
            }
            let models = category.getSlideModels(slideModel, { citeSources: metadata.citeSources }).filter(model => model != null);

            // copy over metadata from the current slide
            models = models.map(model => ({
                ...model,
                metadata: {
                    ...currentSlide.getMetadata(),
                    ...model.metadata,
                    generatedIdeas,
                    aiGenerated: true,
                    slideCategory: idea.slideCategory,
                    slideReasoning: idea.slideReasoning,
                    slideConfidence: idea.slideConfidence,
                    needsLayoutProcessing: true
                }
            }));

            models.forEach(model => {
                let elementsWithImages = findElementsWithImages(model);
                for (let element of elementsWithImages) {
                    if (element.__generateImage) {
                        element.__generateImage.imageStyle = imageStyle;
                        element.__generateImage.imageSource = slideImage?.imageSource ?? imageSource;
                        element.__generateImage.imageModel = imageModel;
                        element.__generateImage.imageQuality = imageQuality;

                        if (imageSource === "none") {
                            element.content_value = null;
                            element.__generateImage.isGenerating = false;
                        } else if (element.__generateImage.isSlideImage) {
                            // The slide image is generated once for the whole slide and never
                            // through generateImagesForSlide() below, so this element can only
                            // mirror the slide image's state. isGenerating is not always set while
                            // it runs - finalizeSlide() writes the slide image with just a null
                            // assetId, and the worker's own guard means it can't be set true there -
                            // so an image with no asset counts as generating unless it is explicitly
                            // finished, which is how a Team Asset miss reports "found nothing"
                            element.__generateImage.isGenerating = !slideImage ||
                                (!slideImage.assetId && slideImage.isGenerating !== false);
                        } else if (!element.content_value) {
                            const key = getGeneratedImageKey(element);
                            const generatedAssetId = key ? generatedIdeaImages[key] : null;

                            if (generatedAssetId) {
                                element.content_value = generatedAssetId;
                                element.__generateImage.isGenerating = false;
                            } else {
                                element.__generateImage.isGenerating = true;
                            }
                        }
                    }
                }
            });

            slideCategories.push({
                slideCategory: category.name,
                slideReasoning: idea.slideReasoning,
                slideConfidence: idea.slideConfidence,
                models
            });
        }

        if (imageSource !== "none") {
            let ideasHash = getObjectHash(generatedIdeas);
            let shouldGenerateImages = this.generatingImagesFor !== ideasHash;
            if (shouldGenerateImages) {
                this.generatingImagesFor = ideasHash;
            }

            slideCategories.forEach((category, categoryIdx) => {
                category.models.forEach((model, idx) => {
                    let elementsWithImages = findElementsWithImages(model);
                    if (shouldGenerateImages && elementsWithImages.some(element => element.__generateImage.isGenerating && !element.__generateImage.isSlideImage)) {
                        AiGenerationService.generateImagesForSlide({ slide: { ...model, presentationId: currentSlide.get("presentationId") } })
                            .then(({ slide }) => {
                                if (this._state.currentSlide !== currentSlide) {
                                    return;
                                }

                                let generatedModel = _.omit(slide, "presentationId");
                                this.persistGeneratedIdeaImages(currentSlide, generatedModel);
                                this._updateState(state => ({
                                    ...state,
                                    slideCategories: state.slideCategories.map((currentCategory, currentCategoryIdx) => {
                                        if (currentCategoryIdx !== categoryIdx) {
                                            return currentCategory;
                                        }

                                        return {
                                            ...currentCategory,
                                            models: currentCategory.models.map((currentModel, currentModelIdx) => {
                                                if (currentModelIdx !== idx) {
                                                    return currentModel;
                                                }

                                                return {
                                                    ...generatedModel,
                                                    slideImage: currentModel.slideImage ?? generatedModel.slideImage,
                                                    metadata: {
                                                        ...currentModel.metadata,
                                                        ...generatedModel.metadata,
                                                    }
                                                };
                                            })
                                        };
                                    })
                                }));
                            });
                    }
                });
            });
        }

        await this._updateState({ slideCategories });
    }

    /**
     * Keeps the asset ids generateImagesForSlide() resolved on the slide so reopening the panel
     * restores them instead of generating new images. Team Asset selection is two model calls with no
     * stable answer, so without this the previews pick a different asset on every open.
     */
    persistGeneratedIdeaImages(slide, model) {
        const metadata = slide.getMetadata();
        const generatedImages = collectGeneratedImages(model);
        const generatedIdeaImages = { ...metadata.generatedIdeaImages };

        const newKeys = Object.keys(generatedImages).filter(key => generatedIdeaImages[key] !== generatedImages[key]);
        if (newKeys.length === 0) {
            return;
        }

        newKeys.forEach(key => {
            generatedIdeaImages[key] = generatedImages[key];
        });

        slide.update({
            metadata: { ...metadata, generatedIdeaImages }
        }, { silent: true, saveUndo: false });
    }

    async parseDocument(file) {
        while (this._state.files.some(f => f.name === file.name)) {
            file = new File([file], `_${file.name}`, { type: file.type });
        }

        await this._updateState(state => ({
            ...state,
            files: [...state.files, { name: file.name, type: file.type, isProcessing: true }]
        }));

        try {
            let parsedFile = await AiGenerationService.parseDocument(file, this._state.files);

            await this._updateState(state => ({
                ...state,
                files: [...state.files.filter(f => f.name !== parsedFile.name), parsedFile]
            }));
        } catch (err) {
            await this._updateState(state => ({
                ...state,
                files: state.files.filter(f => f.name !== file.name)
            }));

            throw err;
        }
    }

    async removeFile(name) {
        await this._updateState(state => ({
            ...state,
            files: state.files.filter(f => f.name !== name)
        }));
    }

    addSourcesToSlide(sources) {
        const { canvasController } = this._state;

        const canvas = canvasController.canvas;
        const model = canvas.model;

        if (!model.elements.elementAttribution) {
            model.elements.elementAttribution = {};
        }
        if (!model.elements.elementAttribution.elementAttribution) {
            model.elements.elementAttribution.elementAttribution = {};
        }
        if (!model.elements.elementAttribution.elementAttribution.blocks) {
            model.elements.elementAttribution.elementAttribution.blocks = [];
        }
        const textBlocks = model.elements.elementAttribution.elementAttribution.blocks.filter(block => block.type === AuthoringBlockType.TEXT);
        if (textBlocks.length === 0) {
            model.elements.elementAttribution.elementAttribution.blocks.push({
                id: uuid(),
                type: AuthoringBlockType.TEXT,
                html: "",
                textStyle: TextStyleType.BODY
            });
        }

        const block = model.elements.elementAttribution.elementAttribution.blocks.find(block => block.type === AuthoringBlockType.TEXT);
        let html = block.html;

        if (!model.layout) {
            model.layout = {};
        }
        if (!model.layout.showElementAttribution) {
            model.layout.showElementAttribution = true;
            // Treat as empty
            html = "";
        }

        block.html = getSourcesHtml(sources, html);

        return canvas.updateCanvasModel();
    }
}

const slideAiController = new SlideAiController(initialState);
export default slideAiController;

// DEV-START
if (import.meta.webpackHot) {
    const reloadIdeas = async () => {
        if (window.isReloadingIdeas) {
            // eslint-disable-next-line no-console
            console.log("[HMR] Already reloading ideas");
            return;
        }

        if (!slideAiController.currentSlide) {
            return;
        }

        // eslint-disable-next-line no-console
        console.log("[HMR] Reloading ideas");

        window.isReloadingIdeas = true;
        try {
            await slideAiController.buildSlideModels();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error("[HMR] Error reloading ideas", err);
        } finally {
            window.isReloadingIdeas = false;
        }
    };

    let isReady = false;
    const onWebpackReload = status => {
        if (status === "ready") {
            isReady = true;
            return;
        }

        if (status === "idle" && isReady) {
            return reloadIdeas();
        }
    };

    import.meta.webpackHot.addStatusHandler(onWebpackReload);

    import.meta.webpackHot.accept(["common/SlideCategories/CategoryController"], () => {
        return reloadIdeas();
    });
}
// DEV-END
